import { useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bike, CalendarDays, Map, ShoppingBag, User } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";

const TABS = [
  { to: "/", key: "nav_bikes", icon: Bike, id: "bikes" },
  { to: "/prenotazioni", key: "nav_bookings", icon: CalendarDays, id: "bookings" },
  { to: "/percorsi", key: "nav_routes", icon: Map, id: "routes" },
  { to: "/profilo", key: "nav_profile", icon: User, id: "profile" },
];

export function LangSwitch({ prefix = "header" }) {
  const { lang, setLang } = useI18n();
  return (
    <div className="inline-flex w-fit bg-[var(--surface-2)] rounded-full p-0.5 text-xs font-bold">
      {["it", "en"].map((l) => (
        <button key={l} data-testid={`${prefix}-lang-${l}`} onClick={() => setLang(l)}
          className={`px-2.5 h-8 rounded-full uppercase transition-colors ${lang === l ? "bg-white text-[var(--brand-primary)] shadow-sm" : "text-[var(--muted)]"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function Layout() {
  const { user } = useAuth();
  const { t } = useI18n();
  const cart = useCart();
  const nav = useNavigate();
  useEffect(() => {
    const after = localStorage.getItem("mn_after_login");
    if (user && after) {
      localStorage.removeItem("mn_after_login");
      nav(after, { replace: true });
    }
  }, [user, nav]);
  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-[var(--border)]">
        <div className="max-w-6xl mx-auto px-5 md:px-8 h-16 flex items-center gap-3">
          <button data-testid="header-logo" onClick={() => nav("/")} className="flex items-center gap-3">
            <img src="/logo.png" alt="E-BIKE 4 ENDURO" className="w-10 h-10 object-contain" />
            <span className="font-extrabold tracking-tight text-lg hidden sm:inline">Mondo Noleggio</span>
          </button>
          <nav className="hidden md:flex items-center gap-1 ml-auto">
            {TABS.map((tab) => (
              <NavLink key={tab.to} to={tab.to} end data-testid={`nav-${tab.id}`}
                className={({ isActive }) => `px-4 h-10 rounded-full flex items-center gap-2 text-sm font-semibold transition-colors ${isActive ? "bg-[var(--brand-secondary)] text-[var(--brand-primary)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)]"}`}>
                <tab.icon size={17} />{t(tab.key)}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto md:ml-2"><LangSwitch /></div>
          <button data-testid="header-cart-button" onClick={() => nav("/checkout")} title={t("cart_title")}
            className="relative w-10 h-10 rounded-full bg-[var(--surface-2)] flex items-center justify-center hover:bg-[var(--brand-tertiary)] transition-colors">
            <ShoppingBag size={18} color="#283a2c" />
            {cart.items.length > 0 && <span data-testid="header-cart-count" className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[var(--brand-primary)] text-white text-[11px] font-extrabold flex items-center justify-center">{cart.items.length}</span>}
          </button>
          <button data-testid="header-profile-button" onClick={() => nav(user ? "/profilo" : "/accedi")}
            className="w-10 h-10 rounded-full bg-[var(--brand-secondary)] flex items-center justify-center overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User size={19} color="#283a2c" />}
          </button>
        </div>
      </header>
      <main className="flex-1 pb-24 md:pb-10"><Outlet /></main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur-xl border-t border-[var(--border)] grid grid-cols-4">
        {TABS.map((tab) => (
          <NavLink key={tab.to} to={tab.to} end data-testid={`tab-${tab.id}`}
            className={({ isActive }) => `h-16 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ${isActive ? "text-[var(--brand-primary)]" : "text-[var(--muted)]"}`}>
            <tab.icon size={22} />{t(tab.key)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
